import { OrganizationProfileModel } from "api";
import { organizationDemoPeriodStatusesEnum } from "helpers/enums";

type UmamiPayload = Record<string, unknown>;
type CompanyType = "CR Start" | "CR Logistics";
type CompanyStatus = "Demo" | "Subscribed" | "Deactivated";
type OrganizationWithAnalyticsData = OrganizationProfileModel & {
    demoData?: {
        status?: number | string;
    };
};

class UmamiManager {
    private profile!: any;
    private organization?: OrganizationWithAnalyticsData;
    private identifiedUserId?: string;
    private pendingUserId?: string;

    constructor() {
        window.addEventListener("umami:loaded", this.identifyPendingUser);
    }

    init(profile: any, organization?: OrganizationProfileModel) {
        this.profile = profile;
        this.organization = organization as OrganizationWithAnalyticsData | undefined;
    }

    track(event: string, payload: UmamiPayload = {}) {
        if (window.umami && this.isEnabled()) {
            window.umami.track(event, this.getPayload(payload));
        }
    }

    /** Associate subsequent Umami activity with the authenticated CargoRun user. */
    identify(userId?: number) {
        if (!this.isEnabled() || userId === undefined) {
            return;
        }

        const nextUserId = String(userId);

        if (nextUserId === this.pendingUserId || (nextUserId === this.identifiedUserId && !this.pendingUserId)) {
            return;
        }

        this.pendingUserId = nextUserId;
        this.identifyPendingUser();
    }

    private identifyPendingUser = () => {
        if (!this.pendingUserId || !window.umami?.identify) {
            return;
        }

        window.umami.identify(this.pendingUserId);
        this.identifiedUserId = this.pendingUserId;
        this.pendingUserId = undefined;
    };

    private isEnabled(): boolean {
        return process.env.APP_ENV === "test" || process.env.APP_ENV === "production";
    }

    private getPayload(payload: UmamiPayload): UmamiPayload {
        const roles = this.profile?.roles;

        return {
            ...payload,
            CompanyID: payload.CompanyID ?? this.profile?.organizationId,
            CompanyName: payload.CompanyName ?? this.profile?.organizationName,
            Name: payload.Name ?? this.profile?.fullName,
            Roles: payload.Roles ?? (Array.isArray(roles) ? roles.join(", ") : undefined),
            CompanyType: payload.CompanyType ?? this.getCompanyType(),
            CompanyStatus: payload.CompanyStatus ?? this.getCompanyStatus(),
            CompanyRegistrationDate: payload.CompanyRegistrationDate ?? this.getCompanyRegistrationDate(),
            Subsystem: payload.Subsystem ?? "CR Логистика",
        };
    }

    private getCompanyType(): CompanyType {
        return this.organization?.registrationStatus ? "CR Start" : "CR Logistics";
    }

    private getCompanyStatus(): CompanyStatus | undefined {
        switch (this.organization?.demoPeriod?.status || this.organization?.demoData?.status) {
            case organizationDemoPeriodStatusesEnum.Active:
                return "Demo";
            case organizationDemoPeriodStatusesEnum.Completed:
                return "Subscribed";
            case organizationDemoPeriodStatusesEnum.Expired:
                return "Deactivated";
            default:
                return undefined;
        }
    }

    private getCompanyRegistrationDate(): string | undefined {
        return this.organization?.createdAt;
    }
}

export default new UmamiManager();
